/**
 * @file ColumnMapper.js
 * @description Step 2 of the bulk QR wizard: map CSV/XLSX columns to the
 * required EV Number and Chassis Number fields.
 *
 * Design notes:
 *  - The two FormControl Selects are each scoped to the full header list so
 *    the user can pick any column for either field.
 *  - A warning Alert fires when both selects point at the same column because
 *    that would make all QR codes identical.
 *  - A data preview table shows the first 5 data rows with the two selected
 *    columns highlighted to confirm the mapping visually before continuing.
 *  - The count summary bar shows how many rows will produce valid QR codes
 *    versus how many will be skipped due to missing values.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BlockIcon from '@material-ui/icons/Block';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    sectionTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    selectFormControl: {
        width: '100%',
    },
    selectLabel: {
        fontWeight: 600,
    },
    previewPaper: {
        overflowX: 'auto',
        marginTop: theme.spacing(3),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    previewTitle: {
        fontWeight: 700,
        padding: theme.spacing(1.5, 2, 0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[100],
    },
    tableHeadCell: {
        fontWeight: 700,
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    tableHeadCellEV: {
        fontWeight: 700,
        fontSize: '0.8rem',
        backgroundColor: 'rgba(67, 65, 158, 0.10)',
        color: '#43419e',
        whiteSpace: 'nowrap',
    },
    tableHeadCellChassis: {
        fontWeight: 700,
        fontSize: '0.8rem',
        backgroundColor: 'rgba(76, 175, 80, 0.10)',
        color: '#388e3c',
        whiteSpace: 'nowrap',
    },
    tableCellEV: {
        backgroundColor: 'rgba(67, 65, 158, 0.05)',
        fontSize: '0.82rem',
    },
    tableCellChassis: {
        backgroundColor: 'rgba(76, 175, 80, 0.05)',
        fontSize: '0.82rem',
    },
    tableCell: {
        fontSize: '0.82rem',
        color: theme.palette.text.secondary,
    },
    countBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        marginTop: theme.spacing(2.5),
    },
    countItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    countValidIcon: {
        color: '#388e3c',
        fontSize: 20,
    },
    countSkippedIcon: {
        color: theme.palette.warning.main,
        fontSize: 20,
    },
    countText: {
        fontWeight: 700,
        fontSize: '0.9rem',
    },
    countLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.82rem',
    },
    warningAlert: {
        marginTop: theme.spacing(1.5),
    },
    divider: {
        height: 24,
        width: 1,
        backgroundColor: theme.palette.divider,
    },
}));

// ─── Constants ────────────────────────────────────────────────────────────────

/** Number of data rows shown in the preview table. */
const PREVIEW_ROW_COUNT = 5;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ColumnMapper lets the user assign spreadsheet columns to the two required
 * fields (EV Number, Chassis Number) and previews the mapping on sample data.
 *
 * @param {object}   props
 * @param {string[]} props.headers            All header names from the parsed file
 * @param {Array<Record<string,string>>} props.rows  All data rows (used for preview)
 * @param {string}   props.evColumn           Currently selected EV Number column
 * @param {string}   props.chassisColumn      Currently selected Chassis Number column
 * @param {function} props.onEvColumnChange   Called with the new EV column name
 * @param {function} props.onChassisColumn    Called with the new Chassis column name
 * @param {number}   props.validCount         Rows that have both fields non-empty
 * @param {number}   props.skippedCount       Rows skipped due to missing values
 * @param {number}   props.totalRows          Total data row count
 */
function ColumnMapper({
    headers,
    rows,
    evColumn,
    chassisColumn,
    onEvColumnChange,
    onChassisColumn,
    validCount,
    skippedCount,
    totalRows,
}) {
    const classes = useStyles();

    const bothSameColumn = evColumn && chassisColumn && evColumn === chassisColumn;
    const previewRows = rows.slice(0, PREVIEW_ROW_COUNT);

    return (
        <Box>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
                Map your columns to the required fields
            </Typography>

            {/* ── Column selectors ─────────────────────────────────────────── */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl
                        variant="outlined"
                        className={classes.selectFormControl}
                    >
                        <InputLabel className={classes.selectLabel} id="ev-column-label">
                            EV Number Column
                        </InputLabel>
                        <Select
                            labelId="ev-column-label"
                            id="ev-column-select"
                            value={evColumn || ''}
                            onChange={(e) => onEvColumnChange(e.target.value)}
                            label="EV Number Column"
                        >
                            <MenuItem value="">
                                <em>— Select a column —</em>
                            </MenuItem>
                            {headers.map((header) => (
                                <MenuItem key={header} value={header}>
                                    {header}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl
                        variant="outlined"
                        className={classes.selectFormControl}
                    >
                        <InputLabel className={classes.selectLabel} id="chassis-column-label">
                            Chassis Number Column
                        </InputLabel>
                        <Select
                            labelId="chassis-column-label"
                            id="chassis-column-select"
                            value={chassisColumn || ''}
                            onChange={(e) => onChassisColumn(e.target.value)}
                            label="Chassis Number Column"
                        >
                            <MenuItem value="">
                                <em>— Select a column —</em>
                            </MenuItem>
                            {headers.map((header) => (
                                <MenuItem key={header} value={header}>
                                    {header}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* ── Same-column warning ───────────────────────────────────────── */}
            {bothSameColumn && (
                <Alert severity="warning" className={classes.warningAlert}>
                    Both fields are mapped to the same column. Please select different
                    columns for EV Number and Chassis Number.
                </Alert>
            )}

            {/* ── Count summary bar ─────────────────────────────────────────── */}
            {(evColumn || chassisColumn) && (
                <Box className={classes.countBar}>
                    <Box className={classes.countItem}>
                        <CheckCircleIcon className={classes.countValidIcon} />
                        <Box>
                            <Typography className={classes.countText}>
                                {validCount.toLocaleString()}
                            </Typography>
                            <Typography className={classes.countLabel}>valid rows</Typography>
                        </Box>
                    </Box>

                    <Box className={classes.divider} />

                    <Box className={classes.countItem}>
                        <BlockIcon className={classes.countSkippedIcon} />
                        <Box>
                            <Typography className={classes.countText}>
                                {skippedCount.toLocaleString()}
                            </Typography>
                            <Typography className={classes.countLabel}>
                                skipped (empty value)
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.divider} />

                    <Box className={classes.countItem}>
                        <Box>
                            <Typography className={classes.countText}>
                                {totalRows.toLocaleString()}
                            </Typography>
                            <Typography className={classes.countLabel}>total rows</Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* ── Data preview table ────────────────────────────────────────── */}
            {previewRows.length > 0 && (
                <Paper className={classes.previewPaper} elevation={0}>
                    <Typography className={classes.previewTitle}>
                        Data preview — first {Math.min(PREVIEW_ROW_COUNT, rows.length)} rows
                    </Typography>
                    <Table size="small">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                {headers.map((header) => {
                                    const isEV = header === evColumn;
                                    const isChassis = header === chassisColumn;
                                    return (
                                        <TableCell
                                            key={header}
                                            className={
                                                isEV
                                                    ? classes.tableHeadCellEV
                                                    : isChassis
                                                    ? classes.tableHeadCellChassis
                                                    : classes.tableHeadCell
                                            }
                                        >
                                            {header}
                                            {isEV && (
                                                <Typography
                                                    component="span"
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        marginLeft: 4,
                                                        opacity: 0.75,
                                                    }}
                                                >
                                                    (EV)
                                                </Typography>
                                            )}
                                            {isChassis && (
                                                <Typography
                                                    component="span"
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        marginLeft: 4,
                                                        opacity: 0.75,
                                                    }}
                                                >
                                                    (Chassis)
                                                </Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewRows.map((row, rowIndex) => (
                                <TableRow key={rowIndex} hover>
                                    {headers.map((header) => {
                                        const isEV = header === evColumn;
                                        const isChassis = header === chassisColumn;
                                        return (
                                            <TableCell
                                                key={header}
                                                className={
                                                    isEV
                                                        ? classes.tableCellEV
                                                        : isChassis
                                                        ? classes.tableCellChassis
                                                        : classes.tableCell
                                                }
                                            >
                                                {row[header] || (
                                                    <Typography
                                                        component="span"
                                                        style={{
                                                            color: '#bbb',
                                                            fontStyle: 'italic',
                                                            fontSize: 'inherit',
                                                        }}
                                                    >
                                                        (empty)
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}

ColumnMapper.propTypes = {
    /** All column header names from the parsed file. */
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** All data rows; only the first 5 are shown in preview. */
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    /** Currently selected column for EV Number (empty string = none). */
    evColumn: PropTypes.string,
    /** Currently selected column for Chassis Number (empty string = none). */
    chassisColumn: PropTypes.string,
    /** Callback when the EV column selection changes. */
    onEvColumnChange: PropTypes.func.isRequired,
    /** Callback when the Chassis column selection changes. */
    onChassisColumn: PropTypes.func.isRequired,
    /** Count of rows that will produce a valid QR code. */
    validCount: PropTypes.number,
    /** Count of rows skipped due to missing EV or Chassis value. */
    skippedCount: PropTypes.number,
    /** Total number of data rows. */
    totalRows: PropTypes.number,
};

ColumnMapper.defaultProps = {
    evColumn: '',
    chassisColumn: '',
    validCount: 0,
    skippedCount: 0,
    totalRows: 0,
};

export default ColumnMapper;
